import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
  action?: ToastAction;
  description?: string;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (
    message: string,
    type?: 'success' | 'error' | 'info',
    duration?: number,
    action?: ToastAction,
    description?: string
  ) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

// Toasts are meant to be read, not glimpsed: floor every non-persistent toast
// (duration === 0 stays sticky) at this length regardless of what a call site
// requests, so a shorter-than-intended value at some call site can't make a
// toast disappear before someone's had a chance to read it and act on it.
const MIN_TOAST_DURATION_MS = 5000;
const DEFAULT_TOAST_DURATION_MS = 6000;

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (
      message: string,
      type: 'success' | 'error' | 'info' = 'success',
      duration: number = DEFAULT_TOAST_DURATION_MS,
      action?: ToastAction,
      description?: string
    ) => {
      const id = Math.random().toString(36).substring(7);
      const resolvedDuration = duration === 0 ? 0 : Math.max(duration, MIN_TOAST_DURATION_MS);
      const toast: Toast = { id, message, type, duration: resolvedDuration, action, description };

      setToasts((prev) => [...prev, toast]);

      // Auto-remove after duration
      if (resolvedDuration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, resolvedDuration);
      }
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};

